import React, { useState } from "react";
import { DownloadIcon } from "lucide-react";
import { LogoMark } from "../components/LogoMark";
import { FAVICON_SVG, LOGO_MARK_SVG } from "../utils/favicon";
import { usePageTitle } from "../hooks/usePageTitle";
import { EYEBROW, H2 } from "../utils/typography";
type Variant = {
  label: string;
  file: string;
  svg: string;
  size: number;
};
const variants: Variant[] = [{
  label: "Favicon · 512×512 · cream background",
  file: "alyssa-retza-favicon-512.png",
  svg: FAVICON_SVG,
  size: 512
}, {
  label: "Logo mark · 512×512 · transparent",
  file: "alyssa-retza-mark-512.png",
  svg: LOGO_MARK_SVG,
  size: 512
}, {
  label: "Logo mark · 1024×1024 · transparent",
  file: "alyssa-retza-mark-1024.png",
  svg: LOGO_MARK_SVG,
  size: 1024
}];

/** Hidden utility page (/logo) for downloading the lettermark as PNG files. Not linked in navigation. */
export function LogoDownload() {
  usePageTitle("Logo files");
  const [error, setError] = useState<string | null>(null);
  const download = async ({
    svg,
    size,
    file
  }: Variant) => {
    setError(null);
    try {
      const img = new Image();
      img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
      await img.decode();
      const canvas = document.createElement("canvas");
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext("2d");
      if (!ctx) throw new Error("no canvas");
      ctx.drawImage(img, 0, 0, size, size);
      const a = document.createElement("a");
      a.href = canvas.toDataURL("image/png");
      a.download = file;
      a.click();
    } catch {
      setError("That download didn't work in this browser. Try another browser, or right-click the preview to save it.");
    }
  };
  return <main id="main-content" tabIndex={-1} className="flex min-h-screen w-full flex-col items-center justify-center bg-cream px-6 pb-24 pt-40" data-id="element-513" data-mp="27xt460xk6h9p">
      <div className="flex h-48 w-48 items-center justify-center rounded-3xl bg-paper md:h-64 md:w-64" data-id="element-514" data-mp="wie6euxizldj">
        <LogoMark className="h-28 w-28 md:h-40 md:w-40" data-id="element-515" data-mp="xzjm8c53tlx9" />
      </div>
      <h1 className={`${H2} mt-10 text-center text-espresso`} data-id="element-516" data-mp="xy8i2b7qyc5m">Logo files</h1>
      <p className="mt-3 max-w-md text-center text-espresso/70" data-id="element-517" data-mp="12yeb9vto06pt">
        Use the favicon file for the upload under Publish.
      </p>
      <ul className="mt-10 flex w-full max-w-md flex-col gap-3" data-id="element-518" data-mp="t0q4g7isb8ra">
        {variants.map(v => <li key={v.file} data-id="element-519" data-mp="f9v45jmcpzzb">
            <button type="button" onClick={() => download(v)} className={`${EYEBROW} flex h-12 w-full items-center justify-between gap-4 rounded-full bg-espresso px-6 text-cream transition-colors duration-150 ease-out hover:bg-rust`} data-id="element-520" data-mp="f8k0rkwtiu8q">
              <span className="truncate" data-id="element-521" data-mp="a9p8lscyntby">{v.label}</span>
              <DownloadIcon className="h-4 w-4 shrink-0" aria-hidden="true" data-id="element-522" data-mp="a85qrxbnpa82" />
            </button>
          </li>)}
      </ul>
      {error && <p role="alert" className="mt-6 max-w-md text-center text-sm text-dusty" data-id="element-523" data-mp="b9edgin03y8">
          {error}
        </p>}
    </main>;
}